// ─────────────────────────────────────────────────────────────────────────────
// selection_polygon.ts — Graphite particle-animated selection box
//
// Wraps the base Polygon with selection-specific rendering:
//   • Nodes are handle positions — each with a role (scale / rotation / move)
//   • Edges connect adjacent handles around the bbox perimeter + spokes
//   • Particles hop between nodes; edges flash as particle trails
//   • Handle shapes depend on role: square (scale), triangle (rotation),
//     circle (move / translate)
//   • All graphite palette — ominous cursed-diary vibe
//
// Rendering is fully separated from simulation:
//   tick(now)    — advance particle system
//   draw(ctx)    — render snapshot to canvas
//   reshape(bbox) — update node positions when selection bbox changes
// ─────────────────────────────────────────────────────────────────────────────

import { Polygon, type PolyNode, type PolyEdge, type PolygonSnapshot, type EdgeTrail } from './polygon';
import type { HandleId, HandlePosition } from './notebook_state';

// ── Handle role classification ───────────────────────────────────────────────

export type HandleRole = 'scale' | 'rotation' | 'move' | 'delete';

const HANDLE_ROLES: Record<HandleId, HandleRole> = {
  tl: 'scale', t: 'scale', tr: 'scale',
  r: 'scale', br: 'scale', b: 'scale',
  bl: 'scale', l: 'scale',
  rot: 'rotation',
  center: 'move',
  del: 'delete',
};

/** Is this handle on a corner (4 corners) vs edge midpoint? */
function isCorner(id: HandleId): boolean {
  return id === 'tl' || id === 'tr' || id === 'br' || id === 'bl';
}

// ── Graphite palette ─────────────────────────────────────────────────────────

const GRAPHITE      = '#7a7a7a';
const GRAPHITE_LITE = '#9a9a9a';   // lighter for mid-edge scale handles
const GRAPHITE_DIM  = 'rgba(122,122,122,0.35)';
const TRAIL_COLOR   = 'rgba(122,122,122,0.45)';
const FILL_COLOR    = 'rgba(100,100,100,0.25)';   // translucent fill for "occupied" state

// ── Handle geometry sizes ────────────────────────────────────────────────────

const SCALE_CORNER_R = 3;    // corner scale handles (same as edge)
const SCALE_EDGE_R   = 3;    // mid-edge scale handles
const ROT_TRI_R      = 5;    // rotation triangle circumradius
const MOVE_CIRCLE_R  = 7;    // translation circle (now below bbox)
const DEL_SIZE       = 5;    // half-size of delete X mark

// ── SelectionPolygon ─────────────────────────────────────────────────────────

export class SelectionPolygon {
  private _poly: Polygon;
  /** Ordered list of handle ids matching polygon node indices. */
  private _handleIds: HandleId[] = [];
  /** Cached handle positions for hit-testing from outside. */
  private _handles: HandlePosition[] = [];
  /** Is the polygon alive (has a selection bbox). */
  private _active = false;

  constructor() {
    // Start with empty graph; reshape() populates it
    this._poly = new Polygon([], [], {
      particleCount: 4,
      jumpProbability: 0.025,
      jumpDurationRange: [90, 180],
      cooldownRange: [400, 1100],
      trailFadeMs: 300,
    });
  }

  get active(): boolean { return this._active; }
  get handles(): HandlePosition[] { return this._handles; }

  // ── Lifecycle ───────────────────────────────────────────────────────────

  /**
   * Set or update the bounding box.  Rebuilds graph topology on first call
   * and merely moves node positions on subsequent calls (so particles are
   * not respawned when the bbox changes smoothly, e.g. during handle drag).
   */
  reshape(handles: HandlePosition[]): void {
    this._handles = handles;
    const ids = handles.map(h => h.id);
    const nodes: PolyNode[] = handles.map(h => ({ x: h.x, y: h.y }));

    // Topology changed? → rebuild
    if (!this._active || ids.length !== this._handleIds.length ||
        ids.some((id, i) => id !== this._handleIds[i])) {
      this._handleIds = ids;
      const edges = buildSelectionEdges(ids);
      // Particles proportional to node count (≈30%, min 2)
      const dynCount = Math.max(2, Math.floor(nodes.length * 0.3));
      this._poly.configure({ particleCount: dynCount });
      this._poly.setGraph(nodes, edges);
      this._active = true;
    } else {
      // Just reposition
      this._poly.updateNodes(nodes);
    }
  }

  /** Clear the polygon (selection dismissed). */
  clear(): void {
    this._active = false;
    this._handleIds = [];
    this._handles = [];
    this._poly.setGraph([], []);
  }

  // ── Per-frame ───────────────────────────────────────────────────────────

  tick(now: number): void {
    if (!this._active) return;
    this._poly.tick(now);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (!this._active) return;
    const snap = this._poly.snapshot();
    this._drawTrails(ctx, snap);
    this._drawHandles(ctx, snap);
  }

  // ── Trail rendering ─────────────────────────────────────────────────────

  private _drawTrails(ctx: CanvasRenderingContext2D, snap: PolygonSnapshot): void {
    ctx.save();
    for (const trail of snap.trails) {
      if (trail.alpha <= 0.01) continue;
      const edge = snap.edges[trail.edgeIdx];
      const na = snap.nodes[edge.a];
      const nb = snap.nodes[edge.b];

      // Direction-aware start/end
      const fromNode = trail.direction === 1 ? na : nb;
      const toNode   = trail.direction === 1 ? nb : na;

      const headX = fromNode.x + (toNode.x - fromNode.x) * trail.head;
      const headY = fromNode.y + (toNode.y - fromNode.y) * trail.head;
      const tailX = fromNode.x + (toNode.x - fromNode.x) * trail.tail;
      const tailY = fromNode.y + (toNode.y - fromNode.y) * trail.tail;

      // Gradient from tail (transparent) to head (opaque)
      const grad = ctx.createLinearGradient(tailX, tailY, headX, headY);
      grad.addColorStop(0, 'rgba(58,58,58,0)');
      grad.addColorStop(1, TRAIL_COLOR);

      ctx.strokeStyle = grad;
      ctx.lineWidth = 0.8;
      ctx.globalAlpha = trail.alpha;
      ctx.beginPath();
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(headX, headY);
      ctx.stroke();
    }
    ctx.restore();
  }

  // ── Handle rendering ────────────────────────────────────────────────────

  private _drawHandles(ctx: CanvasRenderingContext2D, snap: PolygonSnapshot): void {
    ctx.save();
    for (let i = 0; i < this._handleIds.length; i++) {
      const id = this._handleIds[i];
      const node = snap.nodes[i];
      if (!node) continue;
      const occupied = snap.occupied.has(i);
      const role = HANDLE_ROLES[id];

      switch (role) {
        case 'scale':
          this._drawScaleHandle(ctx, node.x, node.y, occupied, isCorner(id));
          break;
        case 'rotation':
          this._drawRotHandle(ctx, node.x, node.y, occupied);
          break;
        case 'move':
          this._drawMoveHandle(ctx, node.x, node.y, occupied);
          break;
        case 'delete':
          this._drawDeleteHandle(ctx, node.x, node.y, occupied);
          break;
      }
    }
    ctx.restore();
  }

  /** Scale handle — square, angled 45 degrees. All same size. */
  private _drawScaleHandle(
    ctx: CanvasRenderingContext2D,
    x: number, y: number,
    filled: boolean,
    _corner: boolean,
  ): void {
    const r = SCALE_EDGE_R;

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(Math.PI / 4); // 45° tilt

    if (filled) {
      ctx.fillStyle = FILL_COLOR;
      ctx.fillRect(-r, -r, r * 2, r * 2);
    }

    ctx.strokeStyle = GRAPHITE;
    ctx.lineWidth = 0.8;
    ctx.strokeRect(-r, -r, r * 2, r * 2);

    ctx.restore();
  }

  /** Rotation handle — equilateral triangle. */
  private _drawRotHandle(
    ctx: CanvasRenderingContext2D,
    x: number, y: number,
    filled: boolean,
  ): void {
    const r = ROT_TRI_R;
    // 3 vertices of equilateral triangle pointing up
    const angles = [-Math.PI / 2, Math.PI / 6, 5 * Math.PI / 6];

    ctx.save();
    ctx.beginPath();
    for (let i = 0; i < 3; i++) {
      const vx = x + r * Math.cos(angles[i]);
      const vy = y + r * Math.sin(angles[i]);
      if (i === 0) ctx.moveTo(vx, vy);
      else ctx.lineTo(vx, vy);
    }
    ctx.closePath();

    if (filled) {
      ctx.fillStyle = FILL_COLOR;
      ctx.fill();
    }

    ctx.strokeStyle = GRAPHITE;
    ctx.lineWidth = 1.0;
    ctx.stroke();
    ctx.restore();
  }

  /** Move / translate handle — circle. */
  private _drawMoveHandle(
    ctx: CanvasRenderingContext2D,
    x: number, y: number,
    filled: boolean,
  ): void {
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, MOVE_CIRCLE_R, 0, Math.PI * 2);

    if (filled) {
      ctx.fillStyle = FILL_COLOR;
      ctx.fill();
    }

    ctx.strokeStyle = GRAPHITE;
    ctx.lineWidth = 1.0;
    ctx.stroke();
    ctx.restore();
  }

  /** Delete handle — X mark inside a circle. */
  private _drawDeleteHandle(
    ctx: CanvasRenderingContext2D,
    x: number, y: number,
    filled: boolean,
  ): void {
    const r = DEL_SIZE;
    ctx.save();

    // Outer circle
    ctx.beginPath();
    ctx.arc(x, y, r + 2, 0, Math.PI * 2);
    if (filled) {
      ctx.fillStyle = FILL_COLOR;
      ctx.fill();
    }
    ctx.strokeStyle = GRAPHITE;
    ctx.lineWidth = 0.8;
    ctx.stroke();

    // X mark
    ctx.strokeStyle = GRAPHITE;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(x - r * 0.6, y - r * 0.6);
    ctx.lineTo(x + r * 0.6, y + r * 0.6);
    ctx.moveTo(x + r * 0.6, y - r * 0.6);
    ctx.lineTo(x - r * 0.6, y + r * 0.6);
    ctx.stroke();

    ctx.restore();
  }
}

// ── Graph topology builder ───────────────────────────────────────────────────

/**
 * Build edges for the selection polygon.  The perimeter handles form a ring
 * (tl→t→tr→r→br→b→bl→l→tl), rotation connects to t, center connects to
 * all four edge-midpoint handles (t, r, b, l) as spokes.
 */
function buildSelectionEdges(ids: HandleId[]): PolyEdge[] {
  const idx = new Map<HandleId, number>();
  ids.forEach((id, i) => idx.set(id, i));

  const edges: PolyEdge[] = [];
  const add = (a: HandleId, b: HandleId) => {
    const ai = idx.get(a), bi = idx.get(b);
    if (ai !== undefined && bi !== undefined) edges.push({ a: ai, b: bi });
  };

  // Perimeter ring (CW)
  add('tl', 't');  add('t', 'tr');
  add('tr', 'r');  add('r', 'br');
  add('br', 'b');  add('b', 'bl');
  add('bl', 'l');  add('l', 'tl');

  // Rotation spoke — top midpoint only
  add('t', 'rot');

  // Move spoke — bottom midpoint only
  add('b', 'center');

  // Delete spoke — connects from tr corner
  add('tr', 'del');

  return edges;
}
